import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { marked } from "marked";
import DOMPurify from "dompurify";
import SEO from "../components/SEO";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Tag,
  Linkedin,
  Share2,
  ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API}/blogs/${slug}`);
        setBlog(response.data);

        // Fetch related blogs from same category
        const relatedRes = await axios.get(
          `${API}/blogs?category=${response.data.category}&limit=3`
        );
        setRelatedBlogs(
          relatedRes.data.filter((b) => b.slug !== slug).slice(0, 2)
        );
      } catch (err) {
        console.error("Error fetching blog:", err);
        setError("Blog post not found");
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const shareOnLinkedIn = () => {
    const url = `https://greyzone-ai.com/blog/${slug}`;
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      "_blank"
    );
  };

  const copyLink = async () => {
    const url = `https://greyzone-ai.com/blog/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      alert("Link copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-20 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="animate-pulse">
            <div className="h-8 bg-neutral-200 rounded w-1/4 mb-8" />
            <div className="h-12 bg-neutral-200 rounded w-3/4 mb-4" />
            <div className="h-6 bg-neutral-200 rounded w-1/2 mb-8" />
            <div className="h-64 bg-neutral-200 rounded-2xl mb-8" />
            <div className="space-y-4">
              <div className="h-4 bg-neutral-200 rounded w-full" />
              <div className="h-4 bg-neutral-200 rounded w-full" />
              <div className="h-4 bg-neutral-200 rounded w-3/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen pt-32 pb-20 bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-4xl text-neutral-900 mb-4">
            Article Not Found
          </h1>
          <p className="text-neutral-600 mb-8">
            The article you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-6 py-3 font-medium hover:bg-primary-light transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Parse markdown content
  const htmlContent = DOMPurify.sanitize(marked.parse(blog.content));

  return (
    <>
      <SEO
        title={blog.meta_title || `${blog.title} | Saloni Shah`}
        description={blog.meta_description || blog.excerpt}
        url={`/blog/${blog.slug}`}
        image={blog.featured_image}
        type="article"
        publishedTime={blog.created_at}
        modifiedTime={blog.updated_at}
        author="Saloni Shah"
        section={blog.category}
        tags={blog.tags}
        keywords={blog.meta_keywords}
      />

      <article className="pt-24 pb-20" data-testid="blog-post">
        {/* Header */}
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="mb-8 animate-fade-in-up">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-primary hover:text-primary-light font-medium transition-colors mb-8"
              data-testid="back-to-blog"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Insights
            </Link>

            {/* Category */}
            <div className="mb-4">
              <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                {blog.category}
              </span>
            </div>

            {/* Title */}
            <h1 className="font-serif font-bold text-4xl md:text-5xl text-neutral-900 leading-tight mb-6">
              {blog.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-neutral-600 text-sm mb-8">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(blog.created_at)}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {blog.read_time}
              </span>
              <span>By Saloni Shah</span>
            </div>

            {/* Featured Image */}
            {blog.featured_image && (
              <div className="aspect-video rounded-2xl overflow-hidden mb-12">
                <img
                  src={blog.featured_image}
                  alt={blog.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-3 animate-fade-in-up animation-delay-100">
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />

              {/* Tags */}
              {blog.tags && blog.tags.length > 0 && (
                <div className="mt-12 pt-8 border-t border-neutral-200">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Tag className="w-4 h-4 text-neutral-500" />
                    {blog.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="bg-neutral-100 text-neutral-600 px-3 py-1 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Share */}
              <div className="mt-8 p-6 bg-neutral-50 rounded-xl">
                <p className="font-medium text-neutral-900 mb-4">
                  Share this article
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={shareOnLinkedIn}
                    className="flex items-center gap-2 bg-[#0A66C2] text-white px-4 py-2 rounded-lg hover:bg-[#004182] transition-colors"
                    data-testid="share-linkedin"
                  >
                    <Linkedin className="w-4 h-4" />
                    LinkedIn
                  </button>
                  <button
                    onClick={copyLink}
                    className="flex items-center gap-2 bg-neutral-200 text-neutral-700 px-4 py-2 rounded-lg hover:bg-neutral-300 transition-colors"
                    data-testid="share-copy"
                  >
                    <Share2 className="w-4 h-4" />
                    Copy Link
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 animate-fade-in-up animation-delay-200">
              <div className="sticky top-24 space-y-6">
                {/* Author */}
                <Link to="/about" className="block">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-neutral-100">
                    <h3 className="font-serif font-semibold text-lg text-neutral-900 mb-4">
                      About the Author
                    </h3>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-primary/20">
                        <img
                          src="/images/saloni-author.png"
                          alt="Saloni Shah"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="font-medium text-neutral-900">Saloni Shah</p>
                        <p className="text-sm text-neutral-600">
                          Compliance Leader
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-neutral-600 leading-relaxed">
                      Lead Compliance Engineer at ResMed with 8+ years of
                      experience in regulated industry.
                    </p>
                  </div>
                </Link>


                {/* Newsletter CTA */}
                <div className="bg-primary/5 rounded-xl p-6">
                  <h3 className="font-serif font-semibold text-lg text-neutral-900 mb-3">
                    Stay Updated
                  </h3>
                  <p className="text-sm text-neutral-600 mb-4">
                    Get the latest regulatory AI insights.
                  </p>
                  <Link
                    to="/about#contact"
                    className="inline-block bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-light transition-colors"
                  >
                    Get in Touch
                  </Link>
                </div>

                {/* Related Posts */}
                {relatedBlogs.length > 0 && (
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-neutral-100">
                    <h3 className="font-serif font-semibold text-lg text-neutral-900 mb-4">
                      Related Articles
                    </h3>
                    <div className="space-y-4">
                      {relatedBlogs.map((related) => (
                        <Link
                          key={related.id}
                          to={`/blog/${related.slug}`}
                          className="block text-sm text-neutral-700 hover:text-primary transition-colors line-clamp-2"
                        >
                          {related.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* More Articles */}
      <section className="py-16 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex justify-between items-center mb-8">
            <h2 className="font-serif font-semibold text-2xl text-neutral-900">
              More Insights
            </h2>
            <Link
              to="/blog"
              className="text-primary hover:text-primary-light font-medium flex items-center gap-2 transition-colors"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
